// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	createGuildSelectComponents,
	type GuildSelectOption,
} from '@app/features/app/components/dialogs/shared/GuildSelectComponents';
import Guilds from '@app/features/guild/state/Guilds';
import {Select} from '@app/features/ui/components/form/Select';
import styles from '@app/features/user/components/modals/tabs/my_profile_tab/ProfileTypeSelector.module.css';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo} from 'react';

interface ProfileTypeSelectorProps {
	selectedGuildId: string | null;
	onChange: (guildId: string | null) => void;
	disabled: boolean;
}
export const ProfileTypeSelector = observer(({selectedGuildId, onChange, disabled}: ProfileTypeSelectorProps) => {
	const {t} = useLingui();
	const guilds = Guilds.getGuilds();
	const guildOptions: Array<GuildSelectOption> = [
		{value: '', label: t`Global Profile`},
		...guilds.map((guild) => ({
			value: guild.id,
			label: guild.name || '',
			icon: guild.icon ?? null,
		})),
	];
	const guildSelectComponents = useMemo(
		() =>
			createGuildSelectComponents({
				styles: {
					optionRow: styles.guildOption,
					valueRow: styles.guildValue,
					rowGlobal: styles.guildOptionGlobal,
					avatar: styles.guildAvatar,
					avatarPlaceholder: styles.guildAvatarPlaceholder,
					label: styles.guildOptionLabel,
				},
			}),
		[],
	);
	const renderGuildRow = useCallback((option: GuildSelectOption) => {
		const isGlobal = !option.value;
		const iconUrl = option.icon ? AvatarUtils.getGuildIconURL({id: option.value, icon: option.icon}) : null;
		const initial = option.label.charAt(0).toUpperCase();
		return (
			<div className={clsx(styles.guildOption, isGlobal && styles.guildOptionGlobal)}>
				{!isGlobal &&
					(iconUrl ? (
						<div className={styles.guildAvatar} style={{backgroundImage: `url(${iconUrl})`}} aria-hidden />
					) : (
						<div className={styles.guildAvatarPlaceholder} aria-hidden>
							{initial}
						</div>
					))}
				<span className={styles.guildOptionLabel}>{option.label}</span>
			</div>
		);
	}, []);
	const renderOption = useCallback((option: GuildSelectOption) => renderGuildRow(option), [renderGuildRow]);
	const renderValue = useCallback(
		(option: GuildSelectOption | null) => {
			if (!option) return null;
			return renderGuildRow(option);
		},
		[renderGuildRow],
	);
	return (
		<div className={styles.container}>
			<Select
				label={t`Profile Type`}
				value={selectedGuildId || ''}
				options={guildOptions}
				onChange={(value) => onChange(value === '' ? null : value)}
				disabled={disabled}
				className={clsx(disabled && styles.disabled)}
				components={guildSelectComponents}
				renderOption={renderOption}
				renderValue={renderValue}
			/>
			{selectedGuildId && (
				<p className={styles.description}>
					<Trans>
						You are editing your per-community profile. This profile will only be visible in this community and will
						override your global profile.
					</Trans>
				</p>
			)}
		</div>
	);
});
